/* =============================================================================
 * Zenyth Group   |   Accessibility Remediation Layer  (Behavior)
 * -----------------------------------------------------------------------------
 * Store          : TruAlchemy
 * Standard       : WCAG 2.2 Level AA
 * File           : zenyth_scripts.js
 * Load           : deferred, linked last in the document head, so it enhances
 *                  the existing markup after parse without blocking render and
 *                  without altering theme behavior (add-only, progressive).
 * Maintained by  : Zenyth Group   .   https://zenythgroup.com
 * Contact        : accessibility@zenythgroup.com
 * -----------------------------------------------------------------------------
 * PURPOSE
 *   Behavioral accessibility corrections applied as progressive enhancement:
 *   keyboard operability, name/role/state wiring (for example aria-expanded
 *   on disclosure triggers), and guards for single-character key shortcuts.
 *
 * TRACEABILITY CONVENTION
 *   Every correction is wrapped in a numbered marker so it can be reviewed
 *   and reverted in isolation:
 *
 *       open   ->  Fix by Zenyth - ZA 42438 (.rte link underline, 1.4.1)
 *       close  ->  END OF - Fix by Zenyth - ZA 42438 (.rte link underline, 1.4.1)
 *
 *   A single correction that resolves more than one issue lists every number
 *   on the marker (for example: #0007, #0012). The human-readable detail for
 *   each number is kept in Zenyth's internal remediation log.
 * =============================================================================
 */
(function () {
  "use strict";

  /* Corrections are registered below as remediation proceeds. */

  /* Fix by Zenyth - ZA 42900 (Safari keyboard stops for links and buttons, 2.1.1) */
  /* Related: assets/zenyth_scripts.js. Safari does not make links or buttons Tab stops unless "Press Tab to
     highlight each item" is on (or Option+Tab is used). The UserWay overlay used to add a workaround for this
     and the stores are removing it, so this keeps keyboard access in Safari without it. An explicit
     tabindex="0" makes an element a Tab stop in Safari and changes nothing in other browsers, so it is added
     only in Safari, only to links and enabled buttons that have no tabindex of their own. No visual change. */
  (function () {
    var ua = navigator.userAgent || "";
    var safari = ua.indexOf("Safari/") > -1 && !/(Chrome|Chromium|CriOS|FxiOS|EdgiOS|Edg|OPR|OPiOS|Android)/.test(ua);
    if (!safari) return;

    var SELECTOR = "a[href]:not([tabindex]), button:not([disabled]):not([tabindex])";

    function mark(root) {
      if (!root || root.nodeType !== 1) return;
      var list = [];
      if (root.matches && root.matches(SELECTOR)) list.push(root);
      var inner = root.querySelectorAll ? root.querySelectorAll(SELECTOR) : [];
      for (var i = 0; i < inner.length; i++) list.push(inner[i]);
      for (var j = 0; j < list.length; j++) {
        list[j].setAttribute("tabindex", "0");
        list[j].setAttribute("data-znt-safari-tab", "");
      }
    }

    function start() {
      mark(document.documentElement);
      var pending = false;
      var queue = [];
      new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var m = mutations[i];
          if (m.type === "attributes") queue.push(m.target);
          else for (var j = 0; j < m.addedNodes.length; j++) queue.push(m.addedNodes[j]);
        }
        if (pending) return;
        pending = true;
        window.setTimeout(function () {
          var items = queue;
          queue = [];
          pending = false;
          for (var k = 0; k < items.length; k++) mark(items[k]);
        }, 100);
      }).observe(document.documentElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["tabindex", "href", "disabled"]
      });
    }

    if (document.readyState !== "loading") start();
    else document.addEventListener("DOMContentLoaded", start);
  })();
  /* END OF - Fix by Zenyth - ZA 42900 (Safari keyboard stops for links and buttons, 2.1.1) */

  /* Fix by Zenyth - ZA 42439 (focus ring on text fields only for keyboard users, 2.4.7) */
  /* Text fields always match :focus-visible, even after a mouse click. This marks the page as "pointer" after a mouse or touch press and
     clears it on a keyboard move (Tab, or Enter/Space/arrows outside a text field), so the ring shows for keyboard users only. If this
     script does not run, the ring simply always shows. */
  (function () {
    var root = document.documentElement;
    function isText(el) {
      return el && el.matches && el.matches("textarea, input:not([type=button]):not([type=submit]):not([type=reset]):not([type=checkbox]):not([type=radio]):not([type=image]):not([type=file]):not([type=range]):not([type=color])");
    }
    document.addEventListener("pointerdown", function () { root.classList.add("znt-pointer"); }, true);
    document.addEventListener("keydown", function (e) {
      var k = e.key;
      if (k === "Tab" || (/^(Enter| |Escape|ArrowUp|ArrowDown|ArrowLeft|ArrowRight)$/.test(k) && !isText(e.target))) {
        root.classList.remove("znt-pointer");
      }
    }, true);
  })();
  /* END OF - Fix by Zenyth - ZA 42439 (focus ring on text fields only for keyboard users, 2.4.7) */

  /* Fix by Zenyth - ZA 41190, 41189, 42409, 41191 (mega menu disclosure) */
  /* Related: snippets/mega-menu.liquid, assets/zenyth_styles.css */
  (function () {
    if (window.__zenythMegaMenuDisclosure) return;
    window.__zenythMegaMenuDisclosure = true;

    function isOpen(mega) {
      return mega.classList.contains("is-active");
    }

    function syncState(mega, btn) {
      // Reflect the theme's real open state; the caret rotation is handled by
      // CSS keyed off this attribute (see zenyth_styles.css #0002).
      btn.setAttribute("aria-expanded", isOpen(mega) ? "true" : "false");
    }

    function enhance(mega) {
      if (!mega || mega.dataset.zenythMegaBound) return;

      // The native control authored in Liquid. Absent -> nothing to wire.
      var btn = mega.querySelector(":scope > button.znt-mega-toggle");
      if (!btn) return;
      if (typeof mega.handleMouseover !== "function" || typeof mega.close !== "function") return;

      mega.dataset.zenythMegaBound = "true";

      // Keyboard focus must NOT open the panel. The theme opens the mega on BOTH
      // focusin and mouseenter; per the disclosure pattern, focus alone should not
      // reveal the panel (the button opens it). Suppress focusin-open only, leaving
      // hover (mouseenter) intact: capture focusin on this element and stop it before
      // the theme's own focusin listener runs. Focus itself is unaffected.
      mega.addEventListener("focusin", function (e) {
        e.stopImmediatePropagation();
      }, true);

      // The top-level <a> is a genuine navigation link and must not carry
      // disclosure state; strip it if any prior state ever left it there.
      var link =
        mega.querySelector(":scope > a.header__menu-item--top") ||
        mega.querySelector(":scope > a");
      if (link) link.removeAttribute("aria-expanded");

      // Reflect the current state up front (hover/focus may already have opened).
      syncState(mega, btn);

      // Toggle through the theme's own open/close; the menu is NOT reimplemented.
      // A mouse user is already hovering, so the panel is open when they click the
      // caret: closing it then reads as "the click did nothing" (or broke). For a
      // mouse pointer a click only opens. A tap on a touch screen is followed by an
      // emulated hover that opens the panel just before the click arrives, so the
      // click would close it again (open and closed in the same tap). We remember
      // whether the panel was open BEFORE the tap: if the tap's own emulated hover
      // opened it, the click keeps it open; if it was already open, the tap closes it.
      // Keyboard (no pointer) still toggles.
      var lastPointer = "";
      var openBeforeTap = false;
      btn.addEventListener("pointerdown", function (e) {
        lastPointer = e.pointerType || "";
        openBeforeTap = isOpen(mega);
      });
      btn.addEventListener("click", function () {
        var pointer = lastPointer;
        var wasOpen = openBeforeTap;
        lastPointer = "";
        openBeforeTap = false;
        if (isOpen(mega)) {
          var tapOpenedIt = (pointer === "touch" || pointer === "pen") && !wasOpen;
          if (pointer !== "mouse" && !tapOpenedIt) mega.close();
        } else {
          mega.handleMouseover();
        }
        syncState(mega, btn);
      });

      // Touch on this theme only: opening the panel shifts the menu item 18px to the
      // right and shrinks this button from 27px to 9px (the theme's own layout, same on
      // the live site). A tap fires an emulated hover that opens the panel, the button
      // moves out from under the finger and the click lands on the SHOP link, which
      // navigates away. So for touch we handle the tap ourselves on touchend (no
      // movement) and cancel the emulated mouse events and click, then toggle once.
      var touchStartX = 0, touchStartY = 0, touchMoved = false;
      btn.addEventListener("touchstart", function (e) {
        var t = e.touches[0];
        touchStartX = t.clientX;
        touchStartY = t.clientY;
        touchMoved = false;
      }, { passive: true });
      btn.addEventListener("touchmove", function (e) {
        var t = e.touches[0];
        if (Math.abs(t.clientX - touchStartX) > 10 || Math.abs(t.clientY - touchStartY) > 10) touchMoved = true;
      }, { passive: true });
      btn.addEventListener("touchend", function (e) {
        if (touchMoved) return;
        if (e.cancelable) e.preventDefault();
        if (isOpen(mega)) {
          mega.close();
        } else {
          mega.handleMouseover();
        }
        syncState(mega, btn);
      }, { passive: false });

      // Escape closes an open panel and returns focus to the control.
      mega.addEventListener("keydown", function (e) {
        if (e.key === "Escape" && isOpen(mega)) {
          mega.close();
          syncState(mega, btn);
          btn.focus();
        }
      });

      // Close when focus leaves the whole item (the theme only closes on
      // mouseleave, which never fires for keyboard users).
      mega.addEventListener("focusout", function () {
        window.setTimeout(function () {
          if (!mega.contains(document.activeElement) && isOpen(mega)) {
            mega.close();
            syncState(mega, btn);
          }
        }, 0);
      });

      // Keep aria-expanded honest when the panel opens or closes by ANY means
      // (hover, focus, or this control).
      new MutationObserver(function () {
        syncState(mega, btn);
      }).observe(mega, { attributes: true, attributeFilter: ["class"] });
    }

    function scan(root) {
      var scope = root && root.querySelectorAll ? root : document;
      var list = scope.querySelectorAll("mega-menu");
      for (var i = 0; i < list.length; i++) enhance(list[i]);
    }

    function start() {
      scan(document);
      // Bind as soon as the theme registers <mega-menu> instead of waiting for a
      // later DOM mutation (that wait was 1 to 3 s on a fast desktop, longer on slow devices).
      if (window.customElements && window.customElements.whenDefined) {
        window.customElements.whenDefined("mega-menu").then(function () { scan(document); });
      }
      // The header can re-render (localization, section reload, theme editor);
      // rebind, debounced. enhance() is guarded so re-scans are safe.
      var t;
      new MutationObserver(function () {
        window.clearTimeout(t);
        t = window.setTimeout(function () {
          scan(document);
        }, 200);
      }).observe(document.documentElement, { childList: true, subtree: true });
    }

    if (document.readyState !== "loading") start();
    else document.addEventListener("DOMContentLoaded", start);
  })();
  /* END OF - Fix by Zenyth - ZA 41190, 41189, 42409, 41191 (mega menu disclosure) */
  /* Fix by Zenyth - ZA 41185 (vendor iframe title, 4.1.2) */
  (function () {
    var TITLES = [{ host: "attn.tv", title: "Sign-up offer" }];

    function name(frame) {
      if (frame.dataset.zntTitled || (frame.getAttribute("title") || "").trim()) return;
      if (frame.getAttribute("aria-hidden") === "true") return;
      var src = frame.getAttribute("src") || "";
      for (var i = 0; i < TITLES.length; i++) {
        if (src.indexOf(TITLES[i].host) !== -1) {
          frame.setAttribute("title", TITLES[i].title);
          frame.dataset.zntTitled = "1";
          return;
        }
      }
    }

    function scan() {
      var list = document.querySelectorAll("iframe");
      for (var i = 0; i < list.length; i++) name(list[i]);
    }

    function start() {
      scan();
      var t;
      new MutationObserver(function () {
        window.clearTimeout(t);
        t = window.setTimeout(scan, 200);
      }).observe(document.documentElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["src"]
      });
    }

    if (document.readyState !== "loading") start();
    else document.addEventListener("DOMContentLoaded", start);
  })();
  /* END OF - Fix by Zenyth - ZA 41185 (vendor iframe title, 4.1.2) */

  /* Fix by Zenyth - ZA 42186, 41197 (PDP gallery hidden slides, 2.4.3 / 1.3.2 / 4.1.2, 2.1.1) */
  (function () {
    var FOCUSABLE = "a[href], button, input, select, textarea, [tabindex]";

    function visibleIn(item, box) {
      var r = item.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) return false;
      var cx = r.left + r.width / 2;
      return cx >= box.left - 1 && cx <= box.right + 1;
    }

    function sync(list) {
      var box = list.getBoundingClientRect();
      var items = list.children;
      for (var i = 0; i < items.length; i++) {
        var li = items[i];
        var show = visibleIn(li, box) || li.contains(document.activeElement);
        var inner = li.querySelectorAll(FOCUSABLE);
        if (show) {
          if (li.getAttribute("data-znt-hidden")) {
            li.removeAttribute("aria-hidden");
            li.removeAttribute("data-znt-hidden");
            for (var j = 0; j < inner.length; j++) {
              var prev = inner[j].getAttribute("data-znt-tabindex");
              if (prev === null) continue;
              if (prev === "") inner[j].removeAttribute("tabindex");
              else inner[j].setAttribute("tabindex", prev);
              inner[j].removeAttribute("data-znt-tabindex");
            }
          }
        } else if (!li.getAttribute("data-znt-hidden")) {
          li.setAttribute("aria-hidden", "true");
          li.setAttribute("data-znt-hidden", "1");
          for (var k = 0; k < inner.length; k++) {
            inner[k].setAttribute("data-znt-tabindex", inner[k].getAttribute("tabindex") || "");
            inner[k].setAttribute("tabindex", "-1");
          }
        }
      }
    }

    function enhance(host) {
      var list = host.querySelector("ul.slider");
      if (!list || list.dataset.zntGalleryVis) return;
      list.dataset.zntGalleryVis = "1";
      var t = 0;
      function queue() {
        window.clearTimeout(t);
        t = window.setTimeout(function () {
          sync(list);
        }, 60);
      }
      list.addEventListener("scroll", queue, { passive: true });
      window.addEventListener("resize", queue);
      new MutationObserver(queue).observe(list, {
        attributes: true,
        attributeFilter: ["class", "style"],
        subtree: true
      });
      sync(list);
    }

    function scan() {
      var hosts = document.querySelectorAll("thumbnail-slider, product-gallery");
      for (var i = 0; i < hosts.length; i++) enhance(hosts[i]);
    }

    if (document.readyState !== "loading") scan();
    else document.addEventListener("DOMContentLoaded", scan);
    window.addEventListener("load", scan);
  })();
  /* END OF - Fix by Zenyth - ZA 42186, 41197 (PDP gallery hidden slides, 2.4.3 / 1.3.2 / 4.1.2, 2.1.1) */

  /* Fix by Zenyth - ZA 42428, 42187 (PDP content tabs, 1.3.1 / 2.1.1 / 4.1.2) */
  (function () {
    var MOBILE = window.matchMedia("(max-width: 749px)");

    function panelIdFor(tab) {
      var m = (tab.getAttribute("onclick") || "").match(/openPDPSectionTab\(\s*event\s*,\s*['"]([^'"]+)['"]/);
      return m ? m[1] : null;
    }

    function setup(nav) {
      if (nav.dataset.zntPdpTabs) return;
      var tabs = [];
      var panels = [];
      var buttons = nav.querySelectorAll("button.tablinks");
      for (var i = 0; i < buttons.length; i++) {
        var id = panelIdFor(buttons[i]);
        var panel = id && document.getElementById(id);
        if (!panel) continue;
        if (!buttons[i].id) buttons[i].id = "znt-pdp-tab-" + id;
        tabs.push(buttons[i]);
        panels.push(panel);
      }
      if (!tabs.length) return;
      nav.dataset.zntPdpTabs = "1";

      function sync() {
        if (MOBILE.matches) return;
        for (var i = 0; i < tabs.length; i++) {
          var on = tabs[i].classList.contains("active");
          tabs[i].setAttribute("aria-selected", on ? "true" : "false");
          tabs[i].setAttribute("tabindex", on ? "0" : "-1");
          panels[i].setAttribute("aria-hidden", on ? "false" : "true");
        }
      }

      function apply() {
        nav.setAttribute("role", "tablist");
        nav.setAttribute("aria-label", "Product details");
        for (var i = 0; i < tabs.length; i++) {
          tabs[i].setAttribute("role", "tab");
          tabs[i].setAttribute("aria-controls", panels[i].id);
          panels[i].setAttribute("role", "tabpanel");
          panels[i].setAttribute("aria-labelledby", tabs[i].id);
          panels[i].setAttribute("tabindex", "0");
        }
        sync();
      }

      function strip() {
        nav.removeAttribute("role");
        nav.removeAttribute("aria-label");
        for (var i = 0; i < tabs.length; i++) {
          ["role", "aria-selected", "aria-controls", "tabindex"].forEach(function (a) { tabs[i].removeAttribute(a); });
          ["role", "aria-labelledby", "tabindex", "aria-hidden"].forEach(function (a) { panels[i].removeAttribute(a); });
        }
      }

      function mode() {
        if (MOBILE.matches) strip();
        else apply();
      }

      // Only tabs actually shown take part (the theme hides e.g. an empty Faqs tab).
      function shown() {
        return tabs.filter(function (t) { return getComputedStyle(t).display !== "none"; });
      }

      tabs.forEach(function (tab) {
        tab.addEventListener("keydown", function (e) {
          if (MOBILE.matches) return;
          var list = shown(), idx = list.indexOf(tab), last = list.length - 1, next;
          if (idx < 0) return;
          if (e.key === "ArrowRight") next = idx === last ? 0 : idx + 1;
          else if (e.key === "ArrowLeft") next = idx === 0 ? last : idx - 1;
          else if (e.key === "Home") next = 0;
          else if (e.key === "End") next = last;
          else return;
          e.preventDefault();
          list[next].focus();
          list[next].click(); // the theme's openPDPSectionTab switches; the observer re-syncs
        });
      });

      new MutationObserver(sync).observe(nav, { attributes: true, attributeFilter: ["class"], subtree: true });
      if (MOBILE.addEventListener) MOBILE.addEventListener("change", mode);
      else if (MOBILE.addListener) MOBILE.addListener(mode);
      var rt;
      window.addEventListener("resize", function () {
        window.clearTimeout(rt);
        rt = window.setTimeout(mode, 150);
      });
      mode();
    }

    function scan() {
      var navs = document.querySelectorAll(".tabs-container");
      for (var i = 0; i < navs.length; i++) setup(navs[i]);
    }

    if (document.readyState !== "loading") scan();
    else document.addEventListener("DOMContentLoaded", scan);
  })();
  /* END OF - Fix by Zenyth - ZA 42428, 42187 (PDP content tabs, 1.3.1 / 2.1.1 / 4.1.2) */

})();
